// The overall attribute type
export enum PvEAttributeType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  BOOL = 'BOOL',
  SELECTION = 'SELECTION',
  PLAIN = 'PLAIN',
}

// The unit family type from attributes
export enum PvEAttributeUnitFamily {
  NONE = 'NONE',
  DISTANCE = 'DISTANCE',
  AREA = 'AREA',
  VOLUME = 'VOLUME',
  CURRENCY = 'CURRENCY',
  SOUND_PRESSURE = 'SOUND_PRESSURE',
  LUMINANCE = 'LUMINANCE',
  TEMPERATURE = 'TEMPERATURE',
  PERCENTAGE = 'PERCENTAGE',
  HUMIDITY = 'HUMIDITY',
  VELOCITY = 'VELOCITY',
  THERMAL_TRANSMITTANCE = 'THERMAL_TRANSMITTANCE',
  WEIGHT = 'WEIGHT',
  ENERGY = 'ENERGY',
}

// The related regions for the attributes
export enum PvEAttributeRegion {
  DE = 'DE',
  CH = 'CH',
  GB = 'GB',
  US = 'US',
  GLOBAL = 'GLOBAL',
}

// The possible attribute units
export enum PvEAttributeUnit {
  // Distance
  MM = 'MM',
  CM = 'CM',
  DM = 'DM',
  M = 'M',
  KM = 'KM',
  IN = 'IN',
  FT = 'FT',
  YD = 'YD',
  MI = 'MI',

  // Area
  MM_2 = 'MM2',
  CM_2 = 'CM2',
  DM_2 = 'DM2',
  M_2 = 'M2',
  KM_2 = 'KM2',
  IN_2 = 'IN2',
  FT_2 = 'FT2',
  YD_2 = 'YD2',
  MI_2 = 'MI2',

  // Volume
  MM_3 = 'MM3',
  CM_3 = 'CM3',
  DM_3 = 'DM3',
  M_3 = 'M3',
  KM_3 = 'KM3',
  IN_3 = 'IN3',
  FT_3 = 'FT3',
  YD_3 = 'YD3',
  MI_3 = 'MI3',

  // Currency
  EURO = '€',
  US_DOLLAR = '$',
  BRITISH_POUND = '£',
  SWISS_FRANC = '₣',

  // Sound & light
  DECIBEL = 'dB',
  CD_TO_M_2 = 'CD/M2',

  // Temperature
  FARENHEIT = '°F',
  CELSIUS = '°C',

  // Misc
  PC = 'PC',
  G_M3 = 'G_M3',
  M_S = 'M_S',
  W_M2K = 'W_M2K',
  M2K_W = 'M2K_W',

  // Weight
  MG = 'MG',
  G = 'G',
  KG = 'KG',
  T = 'T',
  OZ = 'OZ',
  LBS = 'LBS',
  ST = 'ST',
  LT = 'LT',

  // Energy
  KWH = 'KWH',
}

// Mapping from enum keys to normal unit string representations
export const PvAttributeUnitDisplayMap: Record<PvEAttributeUnit, string> = {
  // Distance
  [PvEAttributeUnit.MM]: 'mm',
  [PvEAttributeUnit.CM]: 'cm',
  [PvEAttributeUnit.DM]: 'dm',
  [PvEAttributeUnit.M]: 'm',
  [PvEAttributeUnit.KM]: 'km',
  [PvEAttributeUnit.IN]: 'in',
  [PvEAttributeUnit.FT]: 'ft',
  [PvEAttributeUnit.YD]: 'yd',
  [PvEAttributeUnit.MI]: 'mi',

  // Area
  [PvEAttributeUnit.MM_2]: 'mm²',
  [PvEAttributeUnit.CM_2]: 'cm²',
  [PvEAttributeUnit.DM_2]: 'dm²',
  [PvEAttributeUnit.M_2]: 'm²',
  [PvEAttributeUnit.KM_2]: 'km²',
  [PvEAttributeUnit.IN_2]: 'in²',
  [PvEAttributeUnit.FT_2]: 'ft²',
  [PvEAttributeUnit.YD_2]: 'yd²',
  [PvEAttributeUnit.MI_2]: 'mi²',

  // Volume
  [PvEAttributeUnit.MM_3]: 'mm³',
  [PvEAttributeUnit.CM_3]: 'cm³',
  [PvEAttributeUnit.DM_3]: 'dm³',
  [PvEAttributeUnit.M_3]: 'm³',
  [PvEAttributeUnit.KM_3]: 'km³',
  [PvEAttributeUnit.IN_3]: 'in³',
  [PvEAttributeUnit.FT_3]: 'ft³',
  [PvEAttributeUnit.YD_3]: 'yd³',
  [PvEAttributeUnit.MI_3]: 'mi³',

  // Currency
  [PvEAttributeUnit.EURO]: '€',
  [PvEAttributeUnit.US_DOLLAR]: '$',
  [PvEAttributeUnit.BRITISH_POUND]: '£',
  [PvEAttributeUnit.SWISS_FRANC]: '₣',

  // Sound & light
  [PvEAttributeUnit.DECIBEL]: 'dB',
  [PvEAttributeUnit.CD_TO_M_2]: 'cd/m²',

  // Temperature
  [PvEAttributeUnit.FARENHEIT]: '°F',
  [PvEAttributeUnit.CELSIUS]: '°C',

  // Misc
  [PvEAttributeUnit.PC]: '%',
  [PvEAttributeUnit.G_M3]: 'g/m³',
  [PvEAttributeUnit.M_S]: 'm/s',
  [PvEAttributeUnit.W_M2K]: 'W/m²K',
  [PvEAttributeUnit.M2K_W]: 'm²K/W',

  // Weight
  [PvEAttributeUnit.MG]: 'mg',
  [PvEAttributeUnit.G]: 'g',
  [PvEAttributeUnit.KG]: 'kg',
  [PvEAttributeUnit.T]: 't',
  [PvEAttributeUnit.OZ]: 'oz',
  [PvEAttributeUnit.LBS]: 'lbs',
  [PvEAttributeUnit.ST]: 'st',
  [PvEAttributeUnit.LT]: 'lt',

  // Energy
  [PvEAttributeUnit.KWH]: 'kWh',
};

// Mapping between unit family and unit
export type PvRUnitFamilyToUnit = Record<PvEAttributeUnitFamily, PvEAttributeUnit[]>;

export const PvunitFamilyToUnitRecord: PvRUnitFamilyToUnit = {
  [PvEAttributeUnitFamily.NONE]: [],

  [PvEAttributeUnitFamily.DISTANCE]: [
    PvEAttributeUnit.MM,
    PvEAttributeUnit.CM,
    PvEAttributeUnit.DM,
    PvEAttributeUnit.M,
    PvEAttributeUnit.KM,
    PvEAttributeUnit.IN,
    PvEAttributeUnit.FT,
    PvEAttributeUnit.YD,
    PvEAttributeUnit.MI,
  ],

  [PvEAttributeUnitFamily.AREA]: [
    PvEAttributeUnit.MM_2,
    PvEAttributeUnit.CM_2,
    PvEAttributeUnit.DM_2,
    PvEAttributeUnit.M_2,
    PvEAttributeUnit.KM_2,
    PvEAttributeUnit.IN_2,
    PvEAttributeUnit.FT_2,
    PvEAttributeUnit.YD_2,
    PvEAttributeUnit.MI_2,
  ],

  [PvEAttributeUnitFamily.VOLUME]: [
    PvEAttributeUnit.MM_3,
    PvEAttributeUnit.CM_3,
    PvEAttributeUnit.DM_3,
    PvEAttributeUnit.M_3,
    PvEAttributeUnit.KM_3,
    PvEAttributeUnit.IN_3,
    PvEAttributeUnit.FT_3,
    PvEAttributeUnit.YD_3,
    PvEAttributeUnit.MI_3,
  ],

  [PvEAttributeUnitFamily.CURRENCY]: [
    PvEAttributeUnit.EURO,
    PvEAttributeUnit.US_DOLLAR,
    PvEAttributeUnit.BRITISH_POUND,
    PvEAttributeUnit.SWISS_FRANC,
  ],

  [PvEAttributeUnitFamily.SOUND_PRESSURE]: [PvEAttributeUnit.DECIBEL],
  [PvEAttributeUnitFamily.LUMINANCE]: [PvEAttributeUnit.CD_TO_M_2],
  [PvEAttributeUnitFamily.TEMPERATURE]: [PvEAttributeUnit.FARENHEIT, PvEAttributeUnit.CELSIUS],
  [PvEAttributeUnitFamily.PERCENTAGE]: [PvEAttributeUnit.PC],
  [PvEAttributeUnitFamily.HUMIDITY]: [PvEAttributeUnit.G_M3],
  [PvEAttributeUnitFamily.VELOCITY]: [PvEAttributeUnit.M_S],
  [PvEAttributeUnitFamily.THERMAL_TRANSMITTANCE]: [PvEAttributeUnit.W_M2K, PvEAttributeUnit.M2K_W],

  [PvEAttributeUnitFamily.WEIGHT]: [
    PvEAttributeUnit.MG,
    PvEAttributeUnit.G,
    PvEAttributeUnit.KG,
    PvEAttributeUnit.T,
    PvEAttributeUnit.OZ,
    PvEAttributeUnit.LBS,
    PvEAttributeUnit.ST,
    PvEAttributeUnit.LT,
  ],

  [PvEAttributeUnitFamily.ENERGY]: [PvEAttributeUnit.KWH],
};

// Distance conversion pairs
export const DISTANCE_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [
  [PvEAttributeUnit.MM, PvEAttributeUnit.IN],
  [PvEAttributeUnit.CM, PvEAttributeUnit.IN],
  [PvEAttributeUnit.DM, PvEAttributeUnit.IN],
  [PvEAttributeUnit.M, PvEAttributeUnit.FT],
  [PvEAttributeUnit.KM, PvEAttributeUnit.MI],
];

// Area conversion pairs
export const AREA_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [
  [PvEAttributeUnit.MM_2, PvEAttributeUnit.IN_2],
  [PvEAttributeUnit.CM_2, PvEAttributeUnit.IN_2],
  [PvEAttributeUnit.DM_2, PvEAttributeUnit.IN_2],
  [PvEAttributeUnit.M_2, PvEAttributeUnit.FT_2],
  [PvEAttributeUnit.KM_2, PvEAttributeUnit.MI_2],
];

// Volume conversion pairs
export const VOLUME_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [
  [PvEAttributeUnit.MM_3, PvEAttributeUnit.IN_3],
  [PvEAttributeUnit.CM_3, PvEAttributeUnit.IN_3],
  [PvEAttributeUnit.DM_3, PvEAttributeUnit.IN_3],
  [PvEAttributeUnit.M_3, PvEAttributeUnit.FT_3],
  [PvEAttributeUnit.KM_3, PvEAttributeUnit.MI_3],
];

// Temperature conversion pairs
export const TEMPERATURE_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [[PvEAttributeUnit.CELSIUS, PvEAttributeUnit.FARENHEIT]];

// Weight conversion pairs
export const WEIGHT_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [
  [PvEAttributeUnit.MG, PvEAttributeUnit.OZ],
  [PvEAttributeUnit.G, PvEAttributeUnit.OZ],
  [PvEAttributeUnit.KG, PvEAttributeUnit.LBS],
  [PvEAttributeUnit.T, PvEAttributeUnit.LT],
];

// Other conversion pairs
export const SOUND_PRESSURE_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];
export const LUMINANCE_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];
export const CURRENCY_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];
export const NONE_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];

// New empty conversion maps
export const PERCENTAGE_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];
export const HUMIDITY_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];
export const VELOCITY_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];
export const THERMAL_TRANSMITTANCE_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];
export const ENERGY_UNIT_CONVERSION_MAP: [PvEAttributeUnit, PvEAttributeUnit][] = [];

// Unit Family to conversion pairs
export const UNIT_CONVERSION_MAPS: Record<PvEAttributeUnitFamily, [PvEAttributeUnit, PvEAttributeUnit][]> = {
  [PvEAttributeUnitFamily.NONE]: NONE_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.DISTANCE]: DISTANCE_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.AREA]: AREA_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.VOLUME]: VOLUME_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.TEMPERATURE]: TEMPERATURE_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.CURRENCY]: CURRENCY_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.SOUND_PRESSURE]: SOUND_PRESSURE_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.LUMINANCE]: LUMINANCE_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.PERCENTAGE]: PERCENTAGE_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.HUMIDITY]: HUMIDITY_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.VELOCITY]: VELOCITY_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.THERMAL_TRANSMITTANCE]: THERMAL_TRANSMITTANCE_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.WEIGHT]: WEIGHT_UNIT_CONVERSION_MAP,
  [PvEAttributeUnitFamily.ENERGY]: ENERGY_UNIT_CONVERSION_MAP,
};

// @ts-expect-error todo later
export const METRIC_TO_IMPERIAL: Record<PvEAttributeUnit, number> = {
  // Distance
  [PvEAttributeUnit.MM]: 0.0393701,
  [PvEAttributeUnit.CM]: 0.393701,
  [PvEAttributeUnit.DM]: 3.93701,
  [PvEAttributeUnit.M]: 3.28084,
  [PvEAttributeUnit.KM]: 0.621371,

  // Area
  [PvEAttributeUnit.MM_2]: 0.0015500031,
  [PvEAttributeUnit.CM_2]: 0.15500031,
  [PvEAttributeUnit.DM_2]: 15.500031,
  [PvEAttributeUnit.M_2]: 10.7639,
  [PvEAttributeUnit.KM_2]: 0.386102,

  // Volume
  [PvEAttributeUnit.MM_3]: 0.0000610237,
  [PvEAttributeUnit.CM_3]: 0.0610237,
  [PvEAttributeUnit.DM_3]: 61.0237,
  [PvEAttributeUnit.M_3]: 35.3147,
  [PvEAttributeUnit.KM_3]: 2.39913e8,

  // Weight
  [PvEAttributeUnit.MG]: 0.000035274,
  [PvEAttributeUnit.G]: 0.035274,
  [PvEAttributeUnit.KG]: 2.20462,
  [PvEAttributeUnit.T]: 0.984207,

  // Energy
  [PvEAttributeUnit.KWH]: 1,
};

// @ts-expect-error todo later
export const IMPERIAL_TO_METRIC: Record<PvEAttributeUnit, number> = {
  // Distance
  [PvEAttributeUnit.IN]: 0.0254,
  [PvEAttributeUnit.FT]: 0.3048,
  [PvEAttributeUnit.YD]: 0.9144,
  [PvEAttributeUnit.MI]: 1609.344,

  // Area
  [PvEAttributeUnit.IN_2]: 0.00064516,
  [PvEAttributeUnit.FT_2]: 0.09290304,
  [PvEAttributeUnit.YD_2]: 0.83612736,
  [PvEAttributeUnit.MI_2]: 2.589988e6,

  // Volume
  [PvEAttributeUnit.IN_3]: 1.6387e-5,
  [PvEAttributeUnit.FT_3]: 0.0283168,
  [PvEAttributeUnit.YD_3]: 0.764555,
  [PvEAttributeUnit.MI_3]: 4.16818e9,

  // Weight
  [PvEAttributeUnit.OZ]: 28.3495,
  [PvEAttributeUnit.LBS]: 0.453592,
  [PvEAttributeUnit.ST]: 6.35029,
  [PvEAttributeUnit.LT]: 1.01605,

  // Energy
  [PvEAttributeUnit.KWH]: 1,
};

// Localization of the attribute
export interface PvAttributeAndCategoryLocalization {
  de?: string;
  en?: string;
  fr?: string;
  [languageCode: string]: string | undefined;
}

export type PvLocalizedSelectionValue = Record<string, string>;
export type PvSelectionValue = string | PvLocalizedSelectionValue;

export interface PvDefaultValue {
  value?: number | boolean | string | Array<string> | PvLocalizedSelectionValue | null;
  unit?: string | null;
}

// Default value handling of the attribute
export interface PvAttributeDefaultValue {
  DE?: PvDefaultValue;
  CH?: PvDefaultValue;
  GB?: PvDefaultValue;
  US?: PvDefaultValue;
  GLOBAL?: PvDefaultValue;
  [regionCode: string]: PvDefaultValue | undefined;
}

export interface PvNumberValidation {
  min?: number | null;
  max?: number | null;
  step?: number | null;
  precision?: number | null;
  unit?: string | null;
}

export interface PvTextValidation {
  minLength?: number | null;
  maxLength?: number | null;
  pattern?: string | null;
}

export interface PvSelectionValidation {
  // Legacy: string[]
  // Files API v1.5: Array<{ de: string; en: string; fr: string; ... }>
  values?: PvSelectionValue[];
}

// Validation for type NUMBER / TEXT / SELECTION
export interface PvAttributeValidation {
  DE?: PvNumberValidation | PvTextValidation | PvSelectionValidation;
  CH?: PvNumberValidation | PvTextValidation | PvSelectionValidation;
  GB?: PvNumberValidation | PvTextValidation | PvSelectionValidation;
  US?: PvNumberValidation | PvTextValidation | PvSelectionValidation;
  GLOBAL?: PvNumberValidation | PvTextValidation | PvSelectionValidation;
  [regionCode: string]: PvNumberValidation | PvTextValidation | PvSelectionValidation | undefined;
}

// Necessary type extension for attributes from the files API.
// Attributes can be hidden (not visible to the user) and forced (not changeable by the user),
// but these flags may differ depending on the system using them (e.g., floorplanner, panoviewer).
// Extensions must be handled via lookup tables.
interface PvAttributeExtension {
  mirrorAttributeId?: string;
  hidden?: boolean;
  forced?: boolean;
  biDirectionalDataHandling?: boolean;
  isMirrorAttribute?: boolean;
  showInputField?: boolean;
  order?: number;
  value?: number | boolean | string | Array<string> | PvLocalizedSelectionValue | null;
  displayValue?: number | boolean | string | Array<string> | PvLocalizedSelectionValue | null;
  unit?: string;
  displayUnit?: string;
}

// The attribute type from the files API
export interface PvFilesApiAttribute {
  readonly id: number;
  readonly ownerId: number;
  readonly userId: number;
  readonly publicFlag: boolean;
  readonly key: string;
  readonly type: PvEAttributeType;
  readonly unitFamily: PvEAttributeUnitFamily;
  readonly localization: PvAttributeAndCategoryLocalization;
  defaults: PvAttributeDefaultValue;
  validation: PvAttributeValidation;
  usage: number;
}

// Attribute definition for panoViewer handling
export type PvAttribute = PvFilesApiAttribute & PvAttributeExtension;

// Attributes are part of objects/categories
// Standard-Attributes are dependent for the object type
export enum PvECategoryType {
  Bathtub = 'Bathtub',
  Floor = 'Floor',
  OutdoorFloor = 'OutdoorFloor',
  Roof = 'Roof',
  Ceiling = 'Ceiling',
  Passage = 'Passage',
  Window = 'Window',
  Dormer = 'Dormer',
  Radiator = 'Radiator',
  Space = 'Space',
  Stair = 'Stair',
  Door = 'Door',
  Wall = 'Wall',
  Annotation_Distance = 'Annotation_Distance',
}

// Default object with mapping information
export interface PvDefaultObject {
  id: number;
  objectType: PvECategoryType;
  isOffline?: boolean;
}

// Matching object for the lookup table, used to determine whether a fetched attribute
// should obtain specific properties in relation to the pano viewer.
export interface PvDefaultAttribute {
  id: number;
  hidden: boolean;
  forced: boolean;
  biDirectionalDataHandling: boolean;
  automatic: boolean;
  description: string;
}

// ----------------------------------------------------------------------------------------------------------------
// Objects / Categories
// Objects / Categories are parent structures representing a specific entity type of a structural component
// (for example: Door, Ceiling, etc.).
// Objects / Categories can have 0..n attributes, as represented above.
// For internal use, these objects are interchangeably called "objects" or "categories"
// (they are technically the same). Objects are defined by the selected category.
// ----------------------------------------------------------------------------------------------------------------

// Representation of an Object received by files API
export interface PvCategory {
  id: number;
  ownerId: number;
  publicFlag: boolean;
  name: string;
  ifcObject: {
    ifcClass: string | null;
    ifcType: string | null;
  };
  attributes: Array<PvAttribute>;
  localization: PvAttributeAndCategoryLocalization;
}